"use client";
import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const companyLinks = [
    { id: "mini-money", label: "ميني ماني - Mini Money", href: "#mini-money" },
    { id: "laser-jet", label: "ليزر جيت - LaserJet", href: "#laser-jet-e-shop" },
    { id: "laser-host", label: "ليزر هوست - Laserjet Host", href: "#LaserJet-Host" }
  ];

  const contactLinks = [
    { label: "اتصل بنا", href: "#Contact-us" },
    { label: "ارسال بريد الكتروني", href: "info@laserjet.com.eg" },
  ];

  return (
    <motion.footer 
      initial={{ y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full bg-[#004aad] text-white py-8 md:py-12 px-4 md:px-8 font-sans border-t-4 border-[#3fd5b7]"
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto">
        
        {/* 1️⃣ القسم العلوي: توزيع متناسق يمنع الطول الرأسي على الموبايل */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8 pb-8 border-b border-white/10">
          
          {/* العمود الأول (الوصف والشعار) - يأخذ سطر كامل في الموبايل لتقليل الارتفاع */}
          <div className="col-span-2 lg:col-span-2 flex flex-col space-y-3">
            <div className="w-[140px] relative py-1 bg-white/5 p-2.5 rounded-xl border border-white/10">
              <Image 
                src="/logo2-laser.png" 
                alt="LASERJET Telecom" 
                width={140} 
                height={40}
                className="h-auto brightness-0 invert"
                priority
              />
            </div>
            <p className="text-[11px] md:text-xs text-white/70 leading-relaxed font-medium max-w-md">
              ليزرجيت منصة متكاملة للتسوق الرقمي والخدمات المالية، توفر حلول البيع بالتقسيط المباشر، ودعم التجار، وخدمات الدفع والتحصيل الإلكتروني، بالإضافة إلى حلول البنية التحتية التقنية للشركات.
            </p>
          </div>

          {/* العمود الثاني */}
          <div className="col-span-1">
            <h3 className="text-xs md:text-sm font-bold text-[#3fd5b7] mb-3 md:mb-4 tracking-wide">ليزر جيت</h3>
            <ul className="space-y-2 text-[11px] md:text-xs font-medium text-white/80">
              {companyLinks.map((link, idx) => (
                <li key={idx}>
                  <a href={link.href} className="hover:text-[#3fd5b7] hover:translate-x-1 transition-all inline-block duration-200">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* العمود الثالث */}
          <div className="col-span-1">
            <h3 className="text-xs md:text-sm font-bold text-[#3fd5b7] mb-3 md:mb-4 tracking-wide">اتصل بنا</h3>
            <ul className="space-y-2 text-[11px] md:text-xs font-medium text-white/80">
              {contactLinks.map((link, idx) => (
                <li key={idx}>
                  <a href={link.href} className="hover:text-[#3fd5b7] hover:translate-x-1 transition-all inline-block duration-200">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* العمود الرابع (السوشيال ميديا) */}
          <div className="col-span-2 md:col-span-1 flex flex-col space-y-3">
            <div>
              <h3 className="text-xs font-bold text-white/90 mb-2.5">تواصل معنا</h3>
              <div className="flex items-center gap-2">
                {/* Facebook */}
                <a href="https://www.facebook.com/laserjet.com.eg" target="_blank" rel="noopener noreferrer" className="w-7.5 h-7.5 p-1.5 rounded-lg bg-[#3b5998] hover:scale-105 transition-transform flex items-center justify-center text-white shadow-md">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.8z"/></svg>
                </a>
                {/* X / Twitter */}
                <a href="#" className="w-7.5 h-7.5 p-1.5 rounded-lg bg-black hover:scale-105 transition-transform flex items-center justify-center text-white shadow-md border border-white/10">
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                {/* Youtube */}
                <a href="https://www.youtube.com/@laserjet2458" target="_blank" rel="noopener noreferrer" className="w-7.5 h-7.5 p-1.5 rounded-lg bg-[#ff0000] hover:scale-105 transition-transform flex items-center justify-center text-white shadow-md">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.163a3.003 3.003 0 0 0-2.11-2.11C19.517 3.545 12 3.545 12 3.545s-7.517 0-9.388.508a3.003 3.003 0 0 0-2.11 2.11C0 8.033 0 12 0 12s0 3.967.502 5.837a3.003 3.003 0 0 0 2.11 2.11c1.871.508 9.388.508 9.388.508s7.517 0 9.388-.508a3.003 3.003 0 0 0 2.11-2.11C24 15.967 24 12 24 12s0-3.967-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                {/* Instagram */}
                <a href="https://www.instagram.com/laserjet.com.eg/" target="_blank" rel="noopener noreferrer" className="w-7.5 h-7.5 p-1.5 rounded-lg bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] hover:scale-105 transition-transform flex items-center justify-center text-white shadow-md">
                  <svg className="w-3.5 h-3.5 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                </a>
                {/* Whatsapp */}
                <a href="https://whatsapp.com/channel/0029Va23H37GufIsQw29xG0a" target="_blank" rel="noopener noreferrer" className="w-7.5 h-7.5 p-1.5 rounded-lg bg-[#25D366] hover:scale-105 transition-transform flex items-center justify-center text-white shadow-md">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* 2️⃣ القسم السفلي: مدمج وبمساحات أصغر */}
        <div className="pt-5 flex flex-col items-center text-center space-y-4">
          <p className="text-[10px] md:text-xs text-white/60 max-w-4xl leading-relaxed font-medium bg-white/5 p-3 rounded-xl border border-white/5">
            تلتزم ليزرجيت بتقديم أفضل مستوى من الدعم لعملائها. وفي حال وجود أي شكوى أو استفسار، يُرجى التواصل مباشرةً مع فريق خدمة العملاء على الرقم <span className='text-[#3fd5b7] font-bold'>01004331113</span>، الخاص بمركز خدمة العملاء بالجهاز القومى لتنظيم الاتصالات الذى يعمل من الساعة 8:00 صباحاً وحتى الساعة 10:00 مساءً يومياً.
          </p>
          <div className="text-[10px] md:text-xs text-white/40 font-medium">
            جميع حقوق النشر محفوظة لشركة ليزر جيت © {currentYear}.
          </div>
        </div>

      </div>
    </motion.footer>
  );
}